import { observer } from 'mobx-react';
import ProfileLayout from '../Profile/components/ProfileLayout';
import { useForm, useWatch } from 'react-hook-form';
import moment from 'moment';
import {
    DATE_TIME_YMD, DATE_YMD_ISO,
    SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER, SHARE_KNOWLEDGE_PLAN_CATEGORY_MAPPER,
    SYSTEM_PATH
} from '../../core/configs/constants';
import { CalendarCustom } from '../../components';
import { useStore } from '../../core/utils/hook';
import { useEffect, useMemo, useState } from 'react';
import { flowResult } from 'mobx';
import Pagination from '../Courses/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { canShowShareKnowledgeMenu } from '../../core/utils/gaMenuPermissions';

const ShareKnowledgeRequestListScreen = observer(() => {
    const navigate = useNavigate();

    // store
    const {
        userStore: { userInfoHeader },
        shareKnowledgeStore: { getAllRequestForGA, lstRequest, pagingLstRequest, clearLstRequest }
    } = useStore();

    useEffect(() => {
        if (Object.keys(userInfoHeader).length > 0 && !canShowShareKnowledgeMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
        }
    }, [userInfoHeader]);

    // form
    const { register, watch, getValues, setValue, trigger, control } = useForm();
    const watchFromRequestDate = useWatch({ control, name: 'fromRequestDate' });
    const watchToRequestDate = useWatch({ control, name: 'toRequestDate' });
    const watchPlanType = useWatch({ control, name: 'planType' });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        return () => {
            clearLstRequest();
        }
    }, [watchFromRequestDate, watchToRequestDate, watchPlanType]);

    // function
    const fetchData = async () => {
        setLoading(true);
        await flowResult(getAllRequestForGA(getValues()));
        setLoading(false);
    }

    return (
        <ProfileLayout titlePage={'利用履歴'} type='test-history-screen'>
            <div className='shareknowledge-request-lst-screen'>
                <div className='shareknowledge-title'>利用履歴</div>
                <div className='shareknowledge-filter'>
                    <div className='shareknowledge-filter-date'>
                        <label className='shareknowledge-filter-date-title'>リクエスト日</label>
                        <div className='shareknowledge-filter-date-input'>
                            <CalendarCustom
                                className='w-45'
                                date={watchFromRequestDate || null}
                                displayMode={'date'}
                                onChange={date => {
                                    setValue('fromRequestDate', date ? moment(date).format(DATE_YMD_ISO) : null, { shouldValidate: true });
                                    trigger('fromRequestDate');
                                }}
                            />
                            <span className='flex-grow text-center'>~</span>
                            <CalendarCustom
                                className='w-45'
                                date={watchToRequestDate || null}
                                displayMode={'date'}
                                onChange={date => {
                                    setValue('toRequestDate', date ? moment(date).format(DATE_YMD_ISO) : null, { shouldValidate: true });
                                    trigger('toRequestDate');
                                }}
                            />
                        </div>
                    </div>
                    <div className='shareknowledge-filter-plan'>
                        <label className='shareknowledge-filter-plan-title'>ファイルの種類</label>
                        <div className='shareknowledge-filter-plan-input'>
                            <select {...register('planType')}>
                                <option value=''>全て</option>
                                {
                                    Object.keys(SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER).map((key) => (
                                        <option key={key} value={SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER[key].VALUE.join(',')}>
                                            {SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER[key].LABEL}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                </div>
                <div className='shareknowledge-content'>
                    {
                        loading ? null :
                            lstRequest?.length > 0 ?
                                lstRequest.map((request) => (
                                    <ShareKnowledgeContentItem
                                        key={request.id}
                                        requestDate={request.requestDate}
                                        plan={request.plan}
                                    />
                                ))
                                : <div className='d-flex align-items-center justify-content-center min-height-200'>データはありません</div>
                    }
                </div>
                <Pagination paging={pagingLstRequest} onFetch={fetchData} />
            </div>
        </ProfileLayout>
    )
});

const ShareKnowledgeContentItem = ({ requestDate, plan }) => {
    const fileType = useMemo(() => {
        const type = SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER[Object.keys(SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER).filter(key =>
            SHARE_KNOWLEDGE_PLAN_TYPE_MAPPER[key].VALUE.includes(plan))?.[0]] || null
        const category = SHARE_KNOWLEDGE_PLAN_CATEGORY_MAPPER[Object.keys(SHARE_KNOWLEDGE_PLAN_CATEGORY_MAPPER).filter(key =>
            SHARE_KNOWLEDGE_PLAN_CATEGORY_MAPPER[key].VALUE.includes(plan))?.[0]] || null
        return {
            type,
            category
        }
    }, [plan])

    return (
        <div
            className='shareknowledge-content-item'
            style={{ border: '1px solid #D7D7D7' }}>
            <div className='shareknowledge-content-item-header'>
                <div className='request-date'>
                    <div className='label'>リクエスト日</div>
                    <div className='date'>{requestDate ? moment(requestDate).format(DATE_TIME_YMD) : ''}</div>
                </div>
            </div>
            <div className='shareknowledge-content-item-info'>
                <div className='info-item'>
                    <div className='label'>ファイルの種類</div>
                    <div className='content'>
                        {
                            fileType.type ? fileType.type.LABEL : ''
                        }
                    </div>
                </div>
                <div className='info-item'>
                    <div className='label'>プランの種類</div>
                    <div className='content'>
                        {
                            fileType.category ? fileType.category.LABEL : ''
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ShareKnowledgeRequestListScreen;
